import { useState, useEffect, useRef } from 'react';
import { fetchAddressSuggestions, deleteAddress } from '../services/api';
import useDebounce from './useDebounce';

export const useAddressSuggestions = (pickupNameRef, deliveryNameRef, debounceDelay = 300) => {
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestionsPickup, setShowAddressSuggestionsPickup] = useState(false);
  const [showAddressSuggestionsDelivery, setShowAddressSuggestionsDelivery] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [currentFieldPrefix, setCurrentFieldPrefix] = useState('');
  const skipCityAutocompleteRef = useRef({ pickup: false, delivery: false });

  const debouncedSearchValue = useDebounce(searchValue, debounceDelay);

  useEffect(() => {
    if (debouncedSearchValue && debouncedSearchValue.length >= 3 && currentFieldPrefix) {
      handleFetchAddressSuggestions(debouncedSearchValue);
    }
  }, [debouncedSearchValue, currentFieldPrefix]);

  const handleFetchAddressSuggestions = async value => {
    try {
      if (value) {
        if (value.length < 3) {
          setAddressSuggestions([]);
          return;
        }
        const response = await fetchAddressSuggestions(value);
        setAddressSuggestions(response);
      } else {
        setAddressSuggestions([]);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des suggestions d'adresses:", error);
      setAddressSuggestions([]);
    }
  };

  const handleAddressSuggestionsDisplay = e => {
    const { name, value } = e.target;

    if (name === 'pickup_name') {
      if (value && value.length >= 3) {
        setSearchValue(value);
        setCurrentFieldPrefix('pickup');
        setShowAddressSuggestionsPickup(true);
      } else {
        setShowAddressSuggestionsPickup(false);
      }
    } else if (name === 'delivery_name') {
      if (value && value.length >= 3) {
        setSearchValue(value);
        setCurrentFieldPrefix('delivery');
        setShowAddressSuggestionsDelivery(true);
      } else {
        setShowAddressSuggestionsDelivery(false);
      }
    }
  };

  const handleAddressSuggestionClick = (addressSuggestion, fieldPrefix, formData, setFormData) => {
    if (!addressSuggestion) return;

    skipCityAutocompleteRef.current[fieldPrefix] = true;

    setFormData({
      ...formData,
      [`${fieldPrefix}_name`]: addressSuggestion.name,
      [`${fieldPrefix}_address1`]: addressSuggestion.address1,
      [`${fieldPrefix}_address2`]: addressSuggestion.address2,
      [`${fieldPrefix}_postcode`]: addressSuggestion.postcode,
      [`${fieldPrefix}_city`]: addressSuggestion.city,
      [`${fieldPrefix}_contact`]: addressSuggestion.contact,
    });

    if (fieldPrefix === 'pickup') {
      setShowAddressSuggestionsPickup(false);
    } else if (fieldPrefix === 'delivery') {
      setShowAddressSuggestionsDelivery(false);
    }
    setAddressSuggestions([]);
  };

  const handleDeleteAddressSuggestion = async addressId => {
    try {
      await deleteAddress(addressId);
      setAddressSuggestions(prev => prev.filter(addr => addr.id !== addressId));
    } catch (error) {
      console.error("Erreur lors de la suppression de l'adresse:", error);
    }
  };

  useEffect(() => {
    const handleClickOrTabOutside = event => {
      if (event.type === 'mousedown') {
        if (pickupNameRef.current && !pickupNameRef.current.contains(event.target)) {
          setShowAddressSuggestionsPickup(false);
        }
        if (deliveryNameRef.current && !deliveryNameRef.current.contains(event.target)) {
          setShowAddressSuggestionsDelivery(false);
        }
      } else if (event.type === 'keydown' && event.key === 'Tab') {
        if (pickupNameRef.current && !pickupNameRef.current.contains(event.target)) {
          setShowAddressSuggestionsPickup(false);
        }
        if (deliveryNameRef.current && !deliveryNameRef.current.contains(event.target)) {
          setShowAddressSuggestionsDelivery(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOrTabOutside);
    document.addEventListener('keydown', handleClickOrTabOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOrTabOutside);
      document.removeEventListener('keydown', handleClickOrTabOutside);
    };
  }, [pickupNameRef, deliveryNameRef]);

  return {
    addressSuggestions,
    showAddressSuggestionsPickup,
    showAddressSuggestionsDelivery,
    setShowAddressSuggestionsPickup,
    setShowAddressSuggestionsDelivery,
    handleFetchAddressSuggestions,
    handleAddressSuggestionsDisplay,
    handleAddressSuggestionClick,
    handleDeleteAddressSuggestion,
    skipCityAutocompleteRef,
  };
};
